import React, { Component, createRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import '../../css/PDFModal.css';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

class PDFPreviewer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      imageSrc: null,
      imageWidth: null,
      imageHeight: null,
      imageX: 100,
      imageY: 100,
      dragging: false,
      resizing: false,
      offsetX: 0,
      offsetY: 0,
      pageWidth: 0,
      pageHeight: 0,
      numPages: null,
      scale: 1,
      currentPage: props.imagePlacementParams?.page || 1,
      pendingImageFile: null,
      imagePlacedFromParams: false,
    };
    this.imageRef = createRef();
    this.pdfPageRef = createRef();
    this.pdfDoc = null;
  }

  componentDidMount() {
    if (this.props.imageForSign) {
      this.setState({ pendingImageFile: this.props.imageForSign });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.imageForSign !== this.props.imageForSign &&
      !this.state.imagePlacedFromParams
    ) {
      const { pageWidth, pageHeight } = this.state;
      if (pageWidth && pageHeight) {
        this.loadImageFromFile(this.props.imageForSign);
        this.setState({ imagePlacedFromParams: true });
      } else {
        this.setState({ pendingImageFile: this.props.imageForSign });
      }
    }

    if (prevState.currentPage !== this.state.currentPage && this.pdfDoc) {
      this.loadCurrentPageSize(this.pdfDoc, this.state.currentPage);
    }
  }

  clamp = (val, min, max) => Math.max(min, Math.min(val, max));

  handleDragStart = (e) => {
    this.setState({
      dragging: true,
      offsetX: e.clientX - this.state.imageX,
      offsetY: e.clientY - this.state.imageY,
    });
    document.addEventListener('mousemove', this.handleDragging);
    document.addEventListener('mouseup', this.handleDragEnd);
  };

  handleDragging = (e) => {
    const { offsetX, offsetY, imageWidth, imageHeight, pageWidth, pageHeight } = this.state;
    const textWidth = this.props.qrText ? 190 + 5 : 0;
    const totalBlockWidth = imageWidth + textWidth;
    const newX = this.clamp(e.clientX - offsetX, 0, pageWidth - totalBlockWidth);
    const newY = this.clamp(e.clientY - offsetY, 0, pageHeight - imageHeight);
    this.setState({ imageX: newX, imageY: newY });
  };

  handleDragEnd = () => {
    this.setState({ dragging: false });
    document.removeEventListener('mousemove', this.handleDragging);
    document.removeEventListener('mouseup', this.handleDragEnd);
  };

  handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({
      resizing: true,
      resizeStartWidth: this.state.imageWidth,
      resizeStartHeight: this.state.imageHeight,
      resizeStartX: e.clientX,
    });
    document.addEventListener('mousemove', this.handleResizing);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizing = (e) => {
    const {
      imageX,
      imageY,
      resizeStartX,
      resizeStartWidth,
      resizeStartHeight,
      pageWidth,
      pageHeight,
    } = this.state;

    const deltaX = e.clientX - resizeStartX;
    const scale = (resizeStartWidth + deltaX) / resizeStartWidth;

    let newWidth = resizeStartWidth * scale;
    let newHeight = resizeStartHeight * scale;

    if (newWidth + imageX > pageWidth) {
      newWidth = pageWidth - imageX;
      newHeight = (resizeStartHeight / resizeStartWidth) * newWidth;
    }
    if (newHeight + imageY > pageHeight) {
      newHeight = pageHeight - imageY;
      newWidth = (resizeStartWidth / resizeStartHeight) * newHeight;
    }

    if (newWidth > 20 && newHeight > 20) {
      this.setState({ imageWidth: newWidth, imageHeight: newHeight });
    }
  };

  handleResizeEnd = () => {
    this.setState({ resizing: false });
    document.removeEventListener('mousemove', this.handleResizing);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  };

  handleDocumentLoadSuccess = (pdf) => {
    this.setState({ numPages: pdf.numPages }, () => {
      const pageToLoad = this.state.currentPage || 1;
      this.pdfDoc = pdf;
      this.loadCurrentPageSize(pdf, pageToLoad);
    });
  };

  loadCurrentPageSize = async (pdf, pageNumber) => {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1 });

    const containerPadding = 40;
    const maxWidth = window.innerWidth - containerPadding;
    const maxHeight = window.innerHeight - 150;

    const widthRatio = maxWidth / viewport.width;
    const heightRatio = maxHeight / viewport.height;
    const newScale = Math.min(widthRatio, heightRatio, 1);

    const newPageWidth = viewport.width * newScale;
    const newPageHeight = viewport.height * newScale;

    this.setState(
      {
        scale: newScale,
        pageWidth: newPageWidth,
        pageHeight: newPageHeight,
      },
      () => {
        const { imageSrc, imageWidth, imageHeight } = this.state;

        if (imageSrc && imageWidth && imageHeight) {
          const centerX = (newPageWidth - imageWidth) / 2;
          const centerY = (newPageHeight - imageHeight) / 2;
          this.setState({ imageX: centerX, imageY: centerY });
        }

        if (this.state.pendingImageFile && !this.state.imagePlacedFromParams) {
          requestAnimationFrame(() => {
            this.loadImageFromFile(this.state.pendingImageFile);
            this.setState({
              pendingImageFile: null,
              imagePlacedFromParams: true,
            });
          });
        }
      }
    );
  };

  loadImageFromFile = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const params = this.props.imagePlacementParams;
        const { pageWidth, pageHeight } = this.state;

        const margin = 20;
        const maxWidth = pageWidth - margin * 2;
        const maxHeight = pageHeight - margin * 2;

        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;

        let textWidth = 0;
        let textHeight = 0;
        if (this.props.qrText) {
          const fontSize = 12;
          const lineHeight = fontSize * 1.3;
          const lines = this.props.qrText.split(/\r?\n/);
          textHeight = lines.length * lineHeight;
          textWidth = 190;
        }

        let scaleImage = 1;
        const blockWidth = naturalWidth + (textWidth > 0 ? textWidth + 5 : 0);
        const blockHeight = Math.max(naturalHeight, textHeight);
        if (blockWidth > maxWidth || blockHeight > maxHeight) {
          const widthRatio = maxWidth / blockWidth;
          const heightRatio = maxHeight / blockHeight;
          scaleImage = Math.min(widthRatio, heightRatio);
        }

        const scaledWidth = naturalWidth * scaleImage;
        const scaledHeight = naturalHeight * scaleImage;

        let imageX, imageY, imageWidth, imageHeight;

        if (params) {
          const scaleFactor = this.state.scale;

          imageX = params.x * scaleFactor;
          imageY = pageHeight - params.y * scaleFactor;

          imageWidth = scaledWidth;
          imageHeight = scaledHeight;
        } else {
          imageWidth = scaledWidth;
          imageHeight = scaledHeight;

          const totalBlockWidth =
            scaledWidth + (textWidth > 0 ? textWidth * scaleImage + 5 : 0);

          const totalBlockHeight =
            Math.max(scaledHeight, textHeight * scaleImage);

          imageX = (pageWidth - totalBlockWidth) / 2;
          imageY = (pageHeight - totalBlockHeight) / 2;
        }

        this.setState({
          imageSrc: e.target.result,
          imageX,
          imageY,
          imageWidth,
          imageHeight,
          imagePlacedFromParams: !!params,
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  createTextSVG = (text, width, height) => {
    const fontSize = 12;
    const lineHeight = fontSize * 1.3;
    const textPadding = 10;

    const escape = (str) =>
        str
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

    const lines = [];

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    ctx.font = `${fontSize}px Hauora-Regular, sans-serif`;

    text.split(/\r?\n/).forEach((paragraph) => {
        const words = paragraph.split(/\s+/);

        let line = "";

        words.forEach((word) => {
            const test = line ? `${line} ${word}` : word;

            if (ctx.measureText(test).width > width && line) {
                lines.push(line);
                line = word;
            } else {
                line = test;
            }
        });

        if (line) {
            lines.push(line);
        }
    });

    if (lines.length > 1) {
        lines.splice(1, 0, "");
    }

    const totalHeight = Math.max(
        height,
        lines.length * lineHeight
    );

    let y =
        height / 2 -
        (lines.length * lineHeight) / 2 +
        fontSize;

    const texts = lines
        .map((line) => {
            const t = `
                <text
                    x="${textPadding}"
                    y="${y}"
                    font-family="Hauora-Regular, sans-serif"
                    font-size="${fontSize}"
                    fill="#000">
                    ${escape(line)}
                </text>`;

            y += lineHeight;

            return t;
        })
        .join("");

    return `
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="${width + textPadding}"
            height="${totalHeight}">
            ${texts}
        </svg>`;
};

  generateResizedImage = () => {
    const imageFile = this.props.imageForSign;
    const { qrText, onImageProcessed } = this.props;

    const {
      imageWidth,
      imageHeight,
      imageX,
      imageY,
      pageHeight,
      currentPage,
      scale
    } = this.state;

    if (!imageFile) {
      console.warn("[generateResizedImage] imageFile is empty");
      return;
    }

    const isQrWithText = !!qrText;

    const blockScale = isQrWithText ? 3 : 1;

    const baseFontSize = 8;

    const fontSize =
      baseFontSize * blockScale;

    const lineHeight =
      10 * blockScale;

    const textPadding =
      isQrWithText
        ? 5 * blockScale
        : 0;

    const baseTextWidth = 190;

    const textAreaWidth =
      isQrWithText
        ? baseTextWidth * blockScale
        : 0;

    const qrWidth = Math.round(
      imageWidth * blockScale
    );

    const qrHeight = Math.round(
      imageHeight * blockScale
    );

    let targetWidth;
    let targetHeight;

    if (isQrWithText) {
      targetWidth = Math.round(
        qrWidth +
        textPadding +
        textAreaWidth
      );

      targetHeight = Math.max(
        Math.round(87 * blockScale),
        qrHeight
      );
    } else {
      targetWidth = Math.round(imageWidth);
      targetHeight = Math.round(imageHeight);
    }

    const qrX = 0;

    const qrY = isQrWithText
      ? (targetHeight - qrHeight) / 2
      : 0;

    const textX =
      qrWidth + textPadding;

    const textWidth = isQrWithText
      ? Math.max(
          1,
          targetWidth -
            textX -
            (5 * blockScale)
        )
      : 0;

    console.log(
      "[generateResizedImage] START",
      {
        imageFileName: imageFile.name,
        imageFileType: imageFile.type,
        imageFileSize: imageFile.size,

        type: isQrWithText
          ? "QR_WITH_TEXT"
          : "CUSTOM_IMAGE",

        blockScale,

        previewSize: {
          width: imageWidth,
          height: imageHeight
        },

        finalSize: {
          width: targetWidth,
          height: targetHeight
        },

        qr: {
          x: qrX,
          y: qrY,
          width: qrWidth,
          height: qrHeight
        },

        text: {
          x: textX,
          width: textWidth,
          areaWidth: textAreaWidth,
          fontSize,
          lineHeight,
          textPadding
        },

        position: {
          x: imageX,
          y: imageY
        },

        page: {
          width: this.state.pageWidth,
          height: pageHeight,
          scale
        },

        currentPage,

        qrText
      }
    );

    const objectUrl =
      URL.createObjectURL(imageFile);

    const img = new Image();

    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);

      console.log(
        "[generateResizedImage] Source image loaded:",
        {
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight
        }
      );

      if (
        isQrWithText &&
        document.fonts &&
        document.fonts.ready
      ) {
        try {
          await document.fonts.ready;

          console.log(
            "[generateResizedImage] document.fonts.ready"
          );

          if (document.fonts.check) {
            console.log(
              "[generateResizedImage] Hauora font check:",
              document.fonts.check(
                `${fontSize}px "Hauora-Regular"`
              )
            );
          }
        } catch (err) {
          console.warn(
            "[generateResizedImage] Font loading warning:",
            err
          );
        }
      }

      const sourceWidth =
        img.naturalWidth;

      const sourceHeight =
        img.naturalHeight;

      console.log(
        "[generateResizedImage] Source image:",
        {
          width: sourceWidth,
          height: sourceHeight
        }
      );

      const canvas =
        document.createElement("canvas");

      canvas.width =
        targetWidth;

      canvas.height =
        targetHeight;

      const ctx =
        canvas.getContext("2d", {
          alpha: false
        });

      if (!ctx) {
        console.error(
          "[generateResizedImage] Cannot create canvas context"
        );
        return;
      }

      ctx.fillStyle =
        "#ffffff";

      ctx.fillRect(
        0,
        0,
        targetWidth,
        targetHeight
      );

      ctx.save();

      if (isQrWithText) {
        ctx.imageSmoothingEnabled =
          false;

        ctx.drawImage(
          img,
          0,
          0,
          sourceWidth,
          sourceHeight,
          qrX,
          qrY,
          qrWidth,
          qrHeight
        );
      } else {
        ctx.imageSmoothingEnabled =
          true;

        ctx.drawImage(
          img,
          0,
          0,
          sourceWidth,
          sourceHeight,
          0,
          0,
          targetWidth,
          targetHeight
        );
      }

      ctx.restore();

      console.log(
        "[generateResizedImage] Image rendered:",
        {
          type: isQrWithText
            ? "QR"
            : "CUSTOM",

          width: targetWidth,
          height: targetHeight
        }
      );

      if (isQrWithText && qrText) {
        ctx.save();

        try {
          ctx.fontKerning =
            "normal";
        } catch (e) {}

        try {
          ctx.textRendering =
            "optimizeLegibility";
        } catch (e) {}

        try {
          ctx.fontStretch =
            "normal";
        } catch (e) {}

        ctx.font =
          `${fontSize}px "Hauora-Regular", sans-serif`;

        ctx.fillStyle =
          "#000000";

        ctx.textAlign =
          "left";

        ctx.textBaseline =
          "alphabetic";

        console.log(
          "[generateResizedImage] Text rendering:",
          {
            font:
              ctx.font,

            fontSize,

            lineHeight,

            textX,

            textWidth,

            fontKerning:
              ctx.fontKerning,

            textRendering:
              ctx.textRendering
          }
        );

        const lines = [];

        const paragraphs =
          qrText.split(/\r?\n/);

        paragraphs.forEach(
          (paragraph) => {
            if (!paragraph.trim()) {
              lines.push("");
              return;
            }

            const words =
              paragraph.split(/\s+/);

            let line = "";

            words.forEach(
              (word) => {

                const test =
                  line
                    ? `${line} ${word}`
                    : word;

                const measuredWidth =
                  ctx.measureText(
                    test
                  ).width;

                if (
                  measuredWidth >
                    textWidth &&
                  line
                ) {
                  lines.push(line);

                  line = word;
                } else {
                  line = test;
                }
              }
            );

            if (line) {
              lines.push(line);
            }
          }
        );

        if (
          lines.length > 1 &&
          lines[1] !== ""
        ) {
          lines.splice(
            1,
            0,
            ""
          );
        }

        console.log(
          "[generateResizedImage] Text lines:",
          lines
        );

        const totalTextHeight =
          lines.length *
          lineHeight;

        let textY =
          targetHeight / 2 -
          totalTextHeight / 2 +
          fontSize * 0.8;

        textY += 0.25;

        const renderTextX =
          textX + 0.25;

        lines.forEach((line, index) => {
          const currentFontSize =
            index === 5
              ? 7 * blockScale
              : 8 * blockScale;

          ctx.font =
            `${currentFontSize}px "Hauora-Regular", sans-serif`;

          ctx.fillText(
            line,
            renderTextX,
            textY
          );

          textY += lineHeight;
        });

        ctx.restore();

        console.log(
          "[generateResizedImage] Text rendered"
        );
      }

      console.log(
        "[generateResizedImage] FINAL CANVAS:",
        {
          type: isQrWithText
            ? "QR_WITH_TEXT"
            : "CUSTOM_IMAGE",

          width:
            canvas.width,

          height:
            canvas.height,

          blockScale,

          previewSize: {
            width: imageWidth,
            height: imageHeight
          },

          finalSize: {
            width: targetWidth,
            height: targetHeight
          }
        }
      );

      canvas.toBlob(
        (blob) => {

          if (!blob) {
            console.error(
              "[generateResizedImage] toBlob failed"
            );
            return;
          }

          console.log(
            "[generateResizedImage] Blob created:",
            {
              type:
                blob.type,

              size:
                blob.size
            }
          );

          const mergedFile =
            new File(
              [blob],
              imageFile.name,
              {
                type:
                  "image/png",

                lastModified:
                  Date.now()
              }
            );

          console.log(
            "[generateResizedImage] Final File:",
            {
              name:
                mergedFile.name,

              type:
                mergedFile.type,

              size:
                mergedFile.size
            }
          );

          const checkUrl =
            URL.createObjectURL(
              mergedFile
            );

          const checkImg =
            new Image();

          checkImg.onload = () => {

            console.log(
              "[generateResizedImage] FINAL PNG REAL SIZE:",
              {
                width:
                  checkImg.naturalWidth,

                height:
                  checkImg.naturalHeight,

                expectedWidth:
                  targetWidth,

                expectedHeight:
                  targetHeight
              }
            );

            URL.revokeObjectURL(
              checkUrl
            );
          };

          checkImg.onerror = () => {

            console.error(
              "[generateResizedImage] Cannot verify final PNG"
            );

            URL.revokeObjectURL(
              checkUrl
            );
          };

          checkImg.src =
            checkUrl;

          const pdfX =
            imageX /
            scale;

          const pdfY =
            (
              pageHeight -
              imageY
            ) /
            scale;

          const pdfWidth =
            targetWidth;

          const pdfHeight =
            targetHeight;

          console.log(
            "[generateResizedImage] PDF coordinates:",
            {
              x:
                pdfX,

              y:
                pdfY,

              width:
                pdfWidth,

              height:
                pdfHeight,

              type:
                isQrWithText
                  ? "QR_WITH_TEXT"
                  : "CUSTOM_IMAGE",

              blockScale
            }
          );

          onImageProcessed({
            file:
              mergedFile,

            coords: {
              page:
                currentPage,

              x:
                pdfX,

              y:
                pdfY,

              width:
                pdfWidth,

              height:
                pdfHeight
            }
          });

          console.log(
            "[generateResizedImage] END"
          );
        },

        "image/png"
      );
    };

    img.onerror = () => {

      URL.revokeObjectURL(
        objectUrl
      );

      console.error(
        "[generateResizedImage] Source image load error"
      );
    };


    img.src =
      objectUrl;
  };

  render() {
    const { base64Data, onClose } = this.props;
    const {
      imageSrc,
      imageX,
      imageY,
      imageWidth,
      imageHeight,
      currentPage,
      numPages,
      scale,
    } = this.state;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content-pdf" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={onClose}>×</button>

          <div className="pdf-borders" style={{ position: 'relative' }} ref={this.pdfPageRef}>
            <Document
              file={base64Data}
              onLoadError={(err) => alert('Помилка PDF: ' + err.message)}
              onLoadSuccess={this.handleDocumentLoadSuccess}
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>

            {imageSrc && (
              <div
                ref={this.imageRef}
                id="qr-block"
                style={{
                  position: 'absolute',
                  top: imageY,
                  left: imageX,
                  border: '1px dashed #000',
                  cursor: 'move',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  background: '#fff'
                }}
                onMouseDown={this.handleDragStart}
              >
                <img
                  src={imageSrc}
                  alt="QR"
                  style={{
                    width: imageWidth,
                    height: imageHeight,
                    pointerEvents: 'none',
                    display: 'block',
                  }}
                  draggable={false}
                />

                {this.props.qrText && (
                  <div
                    ref={(el) => (this.qrTextRef = el)}
                    style={{
                      marginLeft: 3,
                      width: 190,
                      fontSize: 8,
                      color: '#000',
                      lineHeight: '1.3em',
                      whiteSpace: 'pre-line',
                      textAlign: 'left',
                      paddingTop: 0
                    }}
                  >
                    {this.props.qrText}
                  </div>
                )}

                {this.props.resizableImage && (
                  <div
                    onMouseDown={this.handleResizeStart}
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: 0,
                      width: 10,
                      height: 10,
                      background: 'blue',
                      cursor: 'nwse-resize',
                    }}
                  />
                )}
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 7,
              marginBottom: 7,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <button className="btn btn-default" onClick={this.generateResizedImage}>
              Зберегти позицію та зображення
            </button>
            <button
              className="btn btn-default"
              onClick={() =>
                this.setState((prev) => ({
                  currentPage: Math.max(1, prev.currentPage - 1),
                }))
              }
              disabled={currentPage <= 1}
            >
              Назад
            </button>
            <span>
              Сторінка {currentPage} з {numPages}
            </span>
            <button
              className="btn btn-default"
              onClick={() =>
                this.setState((prev) => ({
                  currentPage: Math.min(numPages, prev.currentPage + 1),
                }))
              }
              disabled={currentPage >= numPages}
            >
              Вперед
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default PDFPreviewer;